'use client';

import { PropsWithChildren, useEffect, useState } from 'react';
import { datadogRum } from '@datadog/browser-rum';

import ProfileContext from './ProfileContext';

import { Profile } from '@/shared/types';
import { useAuth } from '@/shared/hooks';
import {
  useProfileQuery,
  useGetProfileIdQuery,
} from '@/entities/profile/models';
import { ClientAuth } from '@/shared/auth';

const ProfileProvider = ({ children }: PropsWithChildren) => {
  const { isAuth } = useAuth();
  const [profile, setProfile] = useState<Profile | null>(null);

  const { data: profileIdData, isLoading } = useGetProfileIdQuery(
    isAuth && !!profile
  );
  const { data: profileData } = useProfileQuery({
    yozmitProfileId: profileIdData?.yozmitProfileId,
  });

  const resetProfile = () => {
    if (profile) {
      setProfile(null);
    }
  };

  useEffect(() => {
    if (!isAuth) return resetProfile();

    let isMounted = true;

    const set = async () => {
      const profile = await ClientAuth.getProfile();

      if (!profile || !isMounted) return;

      const { username, email, isStaff } = profile;

      setProfile((prev) => ({
        ...prev,
        name: username,
        email,
        isStaff,
      }));
    };

    set();

    return () => {
      isMounted = false;
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuth]);

  useEffect(() => {
    if (profileIdData && profile) {
      const {
        yozmitProfileId,
        authorProfileId,
        hasNewCollection,
        yozmitOnboardingStatus,
        authorProfileUrlIdentifier,
      } = profileIdData;

      setProfile({
        ...profile,
        yozmitProfileId,
        yozmitOnboardingStatus,
        isAuthor: !!authorProfileId,
        hasNewCollection: hasNewCollection,
        authorIdentifier: authorProfileUrlIdentifier,
      });
    }

    // eslint-disable-next-line
  }, [profileIdData]);

  useEffect(() => {
    if (profileData && profile) {
      setProfile({
        ...profile,
        imgSrc: profileData.imageUrl,
        role: profileData.role,
        roleKor: profileData.roleKor,
      });

      datadogRum.setUser({
        id: String(profileData.id),
        name: profileData.name,
        email: profile.email,
        role: profile.role,
        isAuthor: profile.isAuthor,
        roleKor: profile.roleKor,
        profileImage: profile.imgSrc,
      });
    }

    // eslint-disable-next-line
  }, [profileData]);

  return (
    <ProfileContext.Provider value={{ profile, isLoading }}>
      {children}
    </ProfileContext.Provider>
  );
};

export default ProfileProvider;
